/**
 * Constants for Clink Health Status Tooltips
 *
 * Health statuses indicate the operational state of a clink (campaign link).
 * These tooltips explain what each health status means and possible causes.
 */

import { ClinkHealth, ClinkFromServer } from './types';

/**
 * Icon configuration for each health status
 */
export const CLINK_HEALTH_ICONS = {
  Green: {
    icon: 'fa-check-circle',
    color: '#28a745',
    ariaLabel: 'Healthy status'
  },
  Yellow: {
    icon: 'fa-exclamation-triangle',
    color: '#ffc107',
    ariaLabel: 'Warning status'
  },
  Red: {
    icon: 'fa-times-circle',
    color: '#dc3545',
    ariaLabel: 'Critical status'
  },
  None: {
    icon: 'fa-question-circle',
    color: '#6c757d',
    ariaLabel: 'Unknown status'
  }
} as const;

/**
 * Get tooltip text based on actual clink data
 * This shows relevant information based on what we can determine from frontend data
 */
export function getClinkHealthTooltip(clink: ClinkFromServer | null | undefined): string {
  if (!clink || !clink.health) {
    return 'Status Unknown - Health information is not available for this cLink.';
  }

  const health = clink.health;

  if (health === 'Green') {
    return 'Healthy - This cLink is fully operational and ready to serve traffic. All campaigns are configured correctly and active.';
  }

  if (health === 'None') {
    return 'Status Unknown - Health information is not available for this cLink.';
  }

  if (health === 'Red') {
    // Check if status is ENDED
    if (clink.configs?.[0]?.status?.toUpperCase() === 'ENDED') {
      return 'Critical - This cLink cannot serve traffic because the cLink status is set to ENDED.';
    }

    // Cannot determine exact cause from frontend data
    return 'Critical - This cLink cannot serve traffic. Possible causes include broken campaigns, campaigns removed from feed, or campaigns with end dates that have passed. Click edit to see more details.';
  }

  // For Yellow health - check if we can determine specific cause
  if (health === 'Yellow') {
    // Check if status is PAUSED
    if (clink.configs && clink.configs[0] && clink.configs[0].status?.toUpperCase() === 'PAUSED') {
      return 'Warning - This cLink is currently paused. Change status to Active to resume traffic.';
    }

    // Cannot determine exact cause from frontend data
    return 'Warning - One or more campaigns are inactive. Possible causes include region targeting, pending approval, missing placement whitelist, outside schedule window, missing payout configuration, or campaigns loaded from cache (removed from Pluto but cached up to 60 days). Click edit to see campaign statuses.';
  }

  return 'Status Unknown - Health information is not available for this cLink.';
}

/**
 * Helper function to get icon configuration for a given health status
 */
export function getClinkHealthIcon(health: ClinkHealth | string | undefined) {
  if (!health || !(health in CLINK_HEALTH_ICONS)) {
    return CLINK_HEALTH_ICONS.None;
  }
  return CLINK_HEALTH_ICONS[health as ClinkHealth];
}
